/**
 * "Ảnh thẻ" mode, laid out like the desktop app's "Xử lý ảnh thẻ" tab: numbered settings on the left with the render
 * button at the bottom, the before/after viewer + QC in the middle, the page's "Đã render" and tokens on the right.
 * Everything works without an account; sign-in is asked only when a render is sent (then the same photo and choices
 * are sent automatically once signed in). The result is checked against the spec (QC, "đạt chuẩn").
 */
import { useEffect, useRef, useState } from "react";
import { adsEnabled } from "../ads/enabled";
import { ApiError, type Job, type JobSettings, type Me, type Status } from "../api";
import { AiFields, aiHasTask, chosenModel } from "../components/AiFields";
import { CompareView } from "../components/CompareView";
import { BeautyFields, LightFields, SpecFields, defaultSettings } from "../components/OptionsForm";
import { PhotoPicker } from "../components/PhotoPicker";
import { fetchJobFile, type SheetPart } from "../components/PrintDialog";
import { RenderBar } from "../components/RenderBar";
import { ResultPanel } from "../components/ResultPanel";
import { Section } from "../components/Section";
import { config } from "../config";
import { SharpenCard } from "../editor/SharpenCard";
import { PhotoError, preparePhoto, type PreparedPhoto } from "../image";
import { EDIT, FREE_DAILY, PREMIUM, QUEUE, RENDER, errorMessage } from "../messages";
import { idPhotoQueue, queueOpen } from "../queues";
import { useJob } from "../useJob";
import { SharpenBox } from "./SharpenBox";
import { Side } from "./Side";
import { dropSpecParam, knownSpec, specParam } from "./specLink";
import type { ModeProps } from "./types";
import { codeOf, newKey } from "./util";

const narrow = () => typeof window.matchMedia === "function" && window.matchMedia("(max-width: 899px)").matches;

/** What a press of the render button sends: frozen at the press, so signing in in between changes nothing. */
interface Submission {
  photo: PreparedPhoto;
  settings: JobSettings;
}

interface Block {
  text: string;
  coins?: boolean;
  consent?: boolean;
  /** the consent box of the chosen premium model's provider (section 5) */
  providerConsent?: boolean;
  /** v4: no render machine for the AI edit now: the button is disabled, the reason shown to guests too */
  paused?: boolean;
}

/** Tokens a render with these settings takes: "Chỉnh sửa nâng cao" costs more (server Settings.cost_ai), a premium
 * model costs its own price (top-up tokens only); GĐ4: + one token per extra spec (server api_public.create_job). */
function costOf(me: Me | null, settings: JobSettings | null | undefined): number {
  const premium = chosenModel(me?.premium_models, settings?.ai);
  const extra = (settings?.extra_specs?.length ?? 0) * (me?.cost_per_job ?? 1);
  if (premium) return premium.price_tokens + extra;
  return (settings?.ai ? (me?.cost_ai ?? 5) : (me?.cost_per_job ?? 1)) + extra;
}

/** Why a render cannot be sent for this wallet (null = it can). Consent first: no need to sign in to learn that.
 * v4: an AI edit while no render machine serves its queue (`status`, queues.ts) waits: the server would refuse it. */
function blockFor(me: Me | null, consent: boolean, settings: JobSettings | null | undefined,
                  providerConsents: Record<string, boolean> = {}, status?: Status | null): Block | null {
  if (!consent) return { text: RENDER.needConsent, consent: true };
  if (settings?.ai && !aiHasTask(settings.ai)) return { text: RENDER.needAiTask };
  if (settings?.ai && !queueOpen(status, idPhotoQueue(settings.ai))) return { text: QUEUE.aiPausedRender, paused: true };
  if (!me) return null;
  if (me.locked_until) return { text: errorMessage("account_locked") };
  const cost = costOf(me, settings);
  const premium = chosenModel(me.premium_models, settings?.ai);
  if (premium) {
    if (!premium.available) return { text: RENDER.modelUnavailable };
    if (!providerConsents[premium.provider]) {
      return { text: RENDER.needProviderConsent(premium.provider_label), providerConsent: true };
    }
    const topup = me.topup ?? 0;
    if (!me.unmetered && topup < cost) return { text: RENDER.noTopup(cost, topup), coins: true };
    return null;
  }
  if (me.total < cost) return { text: RENDER.noCoins(cost, me.total, me.daily_allowance), coins: true };
  return null;
}

const CODE = /^[a-z][a-z0-9_]*$/;               // a server warning code (the web words it), not a pipeline sentence

/** GĐ4: the job seen as its n-th spec (1 = the job's own): that spec's files, QC and notes, the same job id. */
export function jobForSpec(job: Job, n: number): Job {
  if (n <= 1) return job;
  const entry = job.qc?.specs?.[n - 2];
  const files = job.files as Record<string, string | undefined>;
  return {
    ...job, settings: { ...job.settings, spec: job.settings.extra_specs?.[n - 2] ?? job.settings.spec }, qc: entry ?? null,
    files: { photo: files[`photo_${n}`], crop: files[`crop_${n}`], local: files[`local_${n}`] },
    warnings: [...job.warnings.filter((w) => CODE.test(w)), ...(entry?.warnings ?? [])],
  };
}

/** Settings as sent: a premium model this account is no longer offered (signed out, tier ended) is dropped. */
function forAccount(me: Me | null, settings: JobSettings): JobSettings {
  if (!settings.ai?.model || chosenModel(me?.premium_models, settings.ai)) return settings;
  const ai = { ...settings.ai };
  delete ai.model;
  return { ...settings, ai };
}

export function IdPhotoMode({ studio, active, open }: ModeProps) {
  const { api, pollMs, me, auth, catalog, consent, providerConsents, sharpen, shared } = studio;
  const [settings, setSettings] = useState<JobSettings | null>(null);
  const [photo, setPhoto] = useState<PreparedPhoto | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [sources, setSources] = useState<Record<string, string>>({}); // job id → preview of the photo it was sent with
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  const [needCoins, setNeedCoins] = useState(false);
  const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);
  const [specIndex, setSpecIndex] = useState(1);   // GĐ4: which spec of the job is shown (1 = the job's own)
  const key = useRef(newKey()); // one Idempotency-Key per submission; kept on network errors so a retry is not charged twice
  const pending = useRef<Submission | null>(null); // the render waiting for the sign-in dialog
  const charged = useRef<{ id: string; cost: number } | null>(null); // the job just paid for (to report a refund)
  const consentBox = useRef<HTMLInputElement>(null);
  const providerConsentBox = useRef<HTMLInputElement>(null);
  const coinsBox = useRef<HTMLElement>(null);
  const viewerBox = useRef<HTMLDivElement>(null);
  const loaded = useRef(0);   // the page's photo (`shared.seq`) this mode shows
  const opened = useRef(0);
  const signedIn = useRef<(m: Me) => void>(() => undefined);
  const photoRef = useRef<(p: PreparedPhoto | null) => void>(() => undefined);
  const [linkedSpec] = useState(specParam);       // `/?spec=<key>` of a /quy-cach guide page (specLink.ts)

  useEffect(() => {
    if (catalog && !settings) {
      setSettings(defaultSettings(catalog, knownSpec(catalog, linkedSpec)));
      dropSpecParam();
    }
  }, [catalog, settings, linkedSpec]);

  const { job, setJob, error: pollError } = useJob(api, activeId, pollMs, (finished) => {
    void (async () => {
      const m = await studio.refreshMe();
      void studio.refreshJobs();
      const paid = charged.current;
      if (paid && paid.id === finished.id) {
        charged.current = null;
        if (finished.status !== "done" && m) setNotice({ text: RENDER.refunded(paid.cost, m.total), ok: true });
      }
    })();
  });

  useEffect(() => setSpecIndex(1), [activeId]);       // another job: its own spec first

  useEffect(() => {                                   // signed out: nothing of that account stays on screen
    if (!studio.epoch) return;
    pending.current = null;
    charged.current = null;
    setActiveId(null);
    setAlert(null);
    setNeedCoins(false);
    setNotice(null);
  }, [studio.epoch]);

  useEffect(() => {                                   // a photo picked (or sharpened) in the other mode
    if (!active || !shared || shared.seq === loaded.current) return;
    loaded.current = shared.seq;
    let stop = false;
    let done = false;
    preparePhoto(shared.files[0], config.maxUploadSide, config.jpegQuality)
      .then((p) => { done = true; if (!stop) photoRef.current(p); })
      .catch((e) => { done = true; if (!stop) setAlert(errorMessage(e instanceof PhotoError ? e.code : "unsupported_photo")); });
    return () => {
      stop = true;
      if (!done) loaded.current = 0;                  // not shown yet: load it again next time
    };
  }, [active, shared]);

  useEffect(() => {                                   // an ID photo chosen in "Đã render"
    if (!open || open.seq === opened.current) return;
    opened.current = open.seq;
    setActiveId(open.job.id);
    if (narrow()) viewerBox.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [open]);

  function show(block: Block) {
    setAlert(block.text);
    setNeedCoins(Boolean(block.coins));
    if (block.consent) consentBox.current?.focus();
    if (block.providerConsent) providerConsentBox.current?.focus();
    if (block.coins) coinsBox.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }

  function askLogin(sub: Submission) {
    pending.current = sub;
    setAlert(null);
    studio.requestLogin({ forRender: true, cost: costOf(me, sub.settings), onSignedIn: (m) => signedIn.current(m),
                          onCancel: () => { pending.current = null; } });  // nothing is sent; photo and choices stay
  }

  async function submit(sub: Submission, cost: number) {
    setSubmitting(true);
    setAlert(null);
    setNeedCoins(false);
    setNotice(null);
    try {
      const created = await api.createJob(sub.photo.blob, sub.settings, key.current);
      key.current = newKey();
      setSources((s) => ({ ...s, [created.id]: sub.photo.previewUrl }));
      setActiveId(created.id);
      setJob(created);
      if (narrow()) viewerBox.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
      const replay = created.created === false;
      if (!replay && cost) charged.current = { id: created.id, cost };
      const m = await studio.refreshMe();
      const sent = !cost ? RENDER.sentFree : m ? RENDER.charged(cost, m.total) : null;
      setNotice(replay ? { text: RENDER.replayed, ok: true } : sent ? { text: sent, ok: true } : null);
      void studio.refreshJobs();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) { // session ended: sign in again, then this same render goes out
        studio.signedOut();
        askLogin(sub);
        return;
      }
      const code = codeOf(e);
      if (code !== "network") key.current = newKey();
      const d = e instanceof ApiError ? e.detail : {};
      setAlert(code === "insufficient_topup" && typeof d.cost === "number"
        ? RENDER.noTopup(d.cost, Number(d.topup ?? 0)) : errorMessage(code));
      setNeedCoins(code === "insufficient_coins" || code === "insufficient_topup");
      void studio.refreshMe();
    } finally {
      setSubmitting(false);
    }
  }

  function onRender() {
    if (!photo || !settings || submitting) return;
    setNotice(null);
    const sub = { photo, settings: forAccount(me, settings) };
    const block = blockFor(me, consent, sub.settings, providerConsents, studio.status);
    if (block) {
      show(block);
      return;
    }
    if (auth !== "in") {
      askLogin(sub);
      return;
    }
    void submit(sub, costOf(me, sub.settings));
  }

  signedIn.current = (m: Me) => {
    const waiting = pending.current;
    pending.current = null;
    if (!waiting) return;
    const sub = { ...waiting, settings: forAccount(m, waiting.settings) };
    const block = blockFor(m, consent, sub.settings, providerConsents, studio.status);
    if (block) show(block);
    else void submit(sub, costOf(m, sub.settings));
  };

  function onPhoto(p: PreparedPhoto | null) {
    if (photo && !Object.values(sources).includes(photo.previewUrl)) URL.revokeObjectURL?.(photo.previewUrl);
    setPhoto(p);
    key.current = newKey();
    setAlert(null);
    setNotice(null);
    setActiveId(null); // the viewer shows the new photo with an empty result, like picking another file on desktop
  }
  photoRef.current = onPhoto;

  function onSettings(s: JobSettings) {
    setSettings(s);
    key.current = newKey();
    setAlert(null);
  }

  function onSharpen() {
    if (!photo) return;
    if (sharpen.send(photo.blob) === EDIT.needConsent) consentBox.current?.focus();
  }

  /** "Chỉnh tiếp ảnh này": the ID photo (as rendered, the spec shown) becomes the editor's photo; this mode keeps its
   * own photo. */
  async function continueInEditor() {
    if (!job) return;
    const blob = await fetchJobFile(api, job, specIndex > 1 ? `photo_${specIndex}` : "photo");
    loaded.current = studio.share([new File([blob], `anh-the-${job.id.slice(0, 8)}.jpg`, { type: "image/jpeg" })]);
    studio.switchMode("pro");
  }

  const shown = settings ? forAccount(me, settings) : settings;
  const cost = costOf(me, shown);
  const costAi = me?.cost_ai ?? 5;
  const costLocal = me?.cost_per_job ?? 1;
  const premium = chosenModel(me?.premium_models, shown?.ai);
  const passive = blockFor(me, consent, shown, providerConsents, studio.status);
  // v4: which render machines are on (queues.ts): section 5 says when the AI edit cannot be taken, the button waits
  const aiPaused = !queueOpen(studio.status, idPhotoQueue(shown?.ai ?? {}));
  const idle = !shown?.ai && !queueOpen(studio.status, "local");      // accepted: the photo waits up to 10 min
  // no photo yet: the disabled button says enough (the dropzone in section 1 is right above)
  const hint = (photo && passive && (auth === "in" || passive.consent || passive.paused) ? passive.text : null)
    ?? (idle ? QUEUE.idle : null);
  const beautyOn = settings ? Object.keys(settings.beauty).length : 0;
  const viewing = activeId !== null;
  // GĐ4: a job of several specs is seen one spec at a time (its photo, crop, QC, downloads)
  const specKeys = job ? [job.settings.spec, ...(job.settings.extra_specs ?? [])] : [];
  const shown1 = job ? jobForSpec(job, Math.min(specIndex, specKeys.length)) : null;
  const specOf = (k: string) => catalog?.specs.find((s) => s.key === k);
  const parts: SheetPart[] | undefined = job?.status === "done" && specKeys.length > 1
    ? specKeys.map((k, i) => ({ label: specOf(k)?.label ?? k, mm: { width: specOf(k)?.width_mm ?? 40, height: specOf(k)?.height_mm ?? 60 },
                                photo: i === 0 ? "photo" : `photo_${i + 1}`, local: i === 0 ? "local" : `local_${i + 1}` }))
    : undefined;
  // done: the render machine's crop of the original, same framing and size as the result (before/after line up)
  const cropUrl = shown1?.status === "done" && shown1.files.crop ? api.fileUrl(shown1.files.crop) : null;
  const shownSpec = catalog?.specs.find((s) => s.key === (shown1?.settings.spec ?? settings?.spec));
  const specRatio = shownSpec ? shownSpec.width_mm / shownSpec.height_mm : 2 / 3;
  const sharpened = Boolean(shared?.sharpened) && shared?.seq === loaded.current;

  return (
    <main className="workspace" hidden={!active}>
      <aside className="settings" aria-label="Cài đặt ảnh thẻ">
        <div className="settings-scroll">
          <Section title="1. Ảnh đầu vào">
            <PhotoPicker photo={photo} consent={consent} consentRef={consentBox}
                         cameraSpec={catalog?.specs.find((s) => s.key === settings?.spec) ?? null}
                         onPhoto={(p, file) => {
                           onPhoto(p);
                           if (p && file) loaded.current = studio.share([file]);
                         }}
                         onConsent={(v) => { studio.setConsent(v); setAlert(null); sharpen.clearMessage(); }} />
            {photo && active && (
              <SharpenBox sharpen={sharpen} sharpened={sharpened} disabled={auth === "loading"} onSend={onSharpen} />
            )}
          </Section>
          {catalog && settings ? (
            <>
              <Section title="2. Quy cách & nền"
                       aside={settings.extra_specs?.length ? `${settings.extra_specs.length + 1} quy cách` : undefined}>
                <SpecFields catalog={catalog} value={settings} onChange={onSettings} />
              </Section>
              <Section title="3. Ánh sáng & da">
                <LightFields catalog={catalog} value={settings} onChange={onSettings} />
              </Section>
              <Section title="4. Làm đẹp (chỉ bật khi cần)" defaultOpen={false}
                       aside={beautyOn > 0 ? `${beautyOn} mục bật` : "đang tắt"}>
                <BeautyFields catalog={catalog} value={settings} onChange={onSettings} />
              </Section>
              <Section title="5. Chỉnh sửa nâng cao" defaultOpen={false}
                       aside={aiPaused ? QUEUE.asidePaused
                         : !settings.ai ? "đang tắt" : cost ? `bật · −${cost} token` : "bật · miễn phí"}>
                <AiFields catalog={catalog} value={settings} onChange={onSettings} paused={aiPaused}
                          cost={settings.ai ? cost : costAi} standardCost={costAi} models={me?.premium_models}
                          consents={providerConsents} consentRef={providerConsentBox}
                          onConsent={(provider, given) => { studio.setProviderConsent(provider, given); setAlert(null); }} />
              </Section>
            </>
          ) : (
            <div className="skeleton" aria-busy="true" aria-label="Đang tải danh sách quy cách" />
          )}
        </div>
        <RenderBar auth={auth} me={me} cost={cost} topupOnly={Boolean(premium)} submitting={submitting}
                   onRender={onRender}
                   disabled={!photo || !settings || auth === "loading" || Boolean(passive?.paused)} hint={hint}
                   alert={alert} notice={notice} />
      </aside>

      <div className="stage">
        <div className="stage-main" ref={viewerBox}>
          {specKeys.length > 1 && (
            <div className="segmented spec-switch" role="group" aria-label="Quy cách của lần render này">
              {specKeys.map((k, i) => (
                <button key={k} type="button" className={`segment${specIndex === i + 1 ? " is-selected" : ""}`}
                        aria-pressed={specIndex === i + 1} onClick={() => setSpecIndex(i + 1)}>
                  {specOf(k)?.label ?? k}
                </button>
              ))}
            </div>
          )}
          <CompareView source={cropUrl ?? (viewing ? sources[activeId] ?? null : photo?.previewUrl ?? null)}
                       sourceTitle={cropUrl ? "Ảnh gốc (cùng khung)" : "Ảnh gốc"} ratio={specRatio}
                       sourceMissing={viewing ? "Ảnh gốc chỉ xem lại được trong lần gửi ảnh này." : "Chọn ảnh ở mục 1 để xem trước."}
                       job={shown1} resultUrl={shown1?.files.photo ? api.fileUrl(shown1.files.photo) : null}
                       localUrl={shown1?.files.local ? api.fileUrl(shown1.files.local) : null} />
          {active && sharpen.job && (
            <SharpenCard api={api} job={sharpen.job} before={sharpen.before} pollError={sharpen.pollError}
                         using={sharpen.using} used={shared?.sharpened === sharpen.job.id} onUse={sharpen.use} />
          )}
          <ResultPanel api={api} job={shown1} pollError={pollError} papers={catalog?.papers} onContinue={continueInEditor}
                       parts={parts} sheetJob={job ?? undefined}
                       onRated={(rated) => { if (job) setJob({ ...job, feedback: rated.feedback }); void studio.refreshJobs(); }}
                       photoMm={shownSpec ? { width: shownSpec.width_mm, height: shownSpec.height_mm } : undefined}
                       onDeleted={(j) => {
                         setJob(j);
                         const paid = charged.current;
                         if (paid && paid.id === j.id && j.status === "cancelled") {
                           charged.current = null;
                           void studio.refreshMe().then((m) => m && setNotice({ text: RENDER.refunded(paid.cost, m.total), ok: true }));
                         } else {
                           void studio.refreshMe();
                         }
                         void studio.refreshJobs();
                       }} />
        </div>
        {active && (
          <Side studio={studio} activeIds={[activeId]} emphasize={needCoins} coinsRef={coinsBox}
                onCoins={() => { setNeedCoins(false); setAlert(null); }}
                hint={<>
                  {me?.unmetered ? "Tài khoản thử nghiệm: render không trừ token. "
                    : `Mỗi lần render trừ ${costLocal} token, có chỉnh sửa nâng cao trừ ${costAi} token. `}
                  {me?.premium_models?.length ? `${PREMIUM.topupOnly} ` : ""}
                  Lúc 0h (giờ Việt Nam) ví free được nạp lại {me?.daily_allowance ?? FREE_DAILY} token, không cộng dồn
                  {adsEnabled() ? "; token nhận từ quảng cáo được giữ lại" : ""}. Token nạp không bị reset, được trừ sau
                  cùng. Ảnh bị từ chối hoặc lỗi được hoàn token.
                </>} />
        )}
      </div>
    </main>
  );
}
